import type { Option } from "@tento-lona/sheets";
import { type DateTime, NaiveDate, SemanticTime, type Utc } from "@tento-chrono";
import type { CellEntry, CellId, CellValue, Json, TimepointCellEntry } from "@tento-lona/sheets";
import type { CellCardinality } from "@tento-lona/sheets/platform";
import type { Actor } from "../invalidation.ts";
import type { CellAddress, LocalCellValue, RowData } from "./row-data.ts";
import { requireNonEmptyKey } from "./_contracts.ts";

/**
 * The write side of a held store (`CellStoreView`), stated here by shape so
 * the sync layer does not import the cells layer above it.
 */
export interface HeldCellWrites {
  dates(range: Option<NaiveDate.Range>): readonly CellEntry[];
  timepoints(range: Option<DateTime.Range<Utc>>): readonly TimepointCellEntry[];
  set(date: NaiveDate.Partial, value: Option<Json | CellValue>, id: Option<string>, actor: Option<Actor>): void;
  clear(date: NaiveDate.Partial, id: Option<string>, actor: Option<Actor>): void;
  setTimepoint(entry: TimepointCellEntry, actor: Option<Actor>): void;
  removeTimepoint(id: string, actor: Option<Actor>): void;
  replaceTimepoints(range: DateTime.Range<Utc>, entries: readonly TimepointCellEntry[], actor: Option<Actor>): void;
  batch<T>(fn: () => T): T;
  install(cells: readonly RowData.Cell[], since: Option<number>): void;
  writeAnswer(cells: readonly RowData.Cell[], actor: Option<Actor>): void;
  timepointOf(cell: RowData.Cell): Option<TimepointCellEntry>;
}

/**
 * What a write needs from the row cache it lands in: the cache's local-write
 * ledger, coverage and notification. The cache implements it once and hands
 * it to both write sides (`CellWrites.own`, `CellWrites.held`).
 */
export interface CellWriteHost {
  /** The identity a date-keyed write stamps (`RowData`'s keying). */
  identityOf(date: NaiveDate.Partial, id: Option<string>): string;
  /** Record a local write against snapshots still parked. */
  recordLocalWrite(identity: string): void;
  /** Drop the computed overlay (the next read recomputes). */
  dropDerived(): void;
  /** The cell's days are really covered now. */
  markRealRange(range: NaiveDate.Range): void;
  /** Extend fetched coverage over `range`. */
  extendFetchedRange(range: DateTime.Range<Utc>): void;
  /** Rebuild indices and fire `range`'s listeners — now, or at the end of the open notification bracket. */
  notify(range: Option<NaiveDate.Range>): void;
  /** Rebuild indices and fire `range`'s listeners (`null`: every listener) at once. */
  rebuildAndFire(range: Option<NaiveDate.Range>): void;
  /** Run a write into the held store; notify `range` unless the store's own change notification reached the cache. */
  viaStore(range: Option<NaiveDate.Range>, write: () => void): void;
  /** Bracket `fn`'s notifications into one. */
  deferred(fn: () => void): void;
  /** The row's cell cardinality. */
  cardinality(): CellCardinality;
}

/**
 * Where a row cache's writes land — one implementation per authority mode,
 * chosen with the read side (`CellAuthority`) when the holder installs or
 * removes a view, so no write re-derives the mode:
 *
 * - `own`: the row's own cells, with the local-write ledger and coverage
 *   kept by the cache.
 * - `held`: the holding sheet's store; the cache hears the change back from
 *   the store's link (or notifies itself when it is not linked).
 */
export interface CellWrites {
  /** Upsert the date-keyed cell; `actor` stamps a local write (`null`: an external edit). */
  setDate(date: NaiveDate.Partial, data: LocalCellValue, id: Option<string>, actor: Option<Actor>): void;
  /** Upsert several date-keyed cells as one change. */
  setDates(edits: readonly { readonly date: NaiveDate.Partial; readonly data: Json | CellValue }[]): void;
  /** Optionally remove `removeId`, then upsert one timepoint cell; `stamp` records local writes. */
  setTimepoint(removeId: Option<CellId>, dtr: DateTime.Range<Utc>, id: CellId, data: Json | CellValue, stamp: boolean): void;
  /** Move one timepoint cell (remove + upsert) at once. */
  moveTimepoint(newRange: DateTime.Range<Utc>, id: CellId, data: Json | CellValue): void;
  /** Remove one timepoint cell by id; `range` is the window it was in. */
  clearTimepoint(id: CellId, range: DateTime.Range<Utc>, stamp: boolean): void;
  /** Replace every timepoint cell in `range` with `cells`. */
  replaceTimepoints(range: DateTime.Range<Utc>, cells: readonly RowData.Cell[]): void;
  /** Apply a timepoint write's answer (optionally removing `removeId`), stamped with `actor`. */
  writeAnswer(removeId: Option<CellId>, cells: readonly RowData.Cell[], actor: Option<Actor>): void;
  /** Where the cell `id` lives now (see `RowData.addressOfExisting`). */
  addressOfExisting(id: CellId, date: NaiveDate.Partial): Option<CellAddress>;
  /** Remove the cell at `addr`; a timepoint's range, else `null`. */
  remove(addr: CellAddress): Option<DateTime.Range<Utc>>;
  /** A server read (`decoded` on demand) installs into this side's store (the own side: nothing; the read already landed in the row's cells). */
  installRead(decoded: () => readonly RowData.Cell[]): void;
}

/** The two write sides (`own`, `held`). */
export namespace CellWrites {
  /** Writes into the row's own cells (`fetched`). */
  export function own(fetched: RowData, host: CellWriteHost): CellWrites {
    return new OwnWrites(fetched, host);
  }

  /** Writes into the holding sheet's store `view`. */
  export function held(view: HeldCellWrites, host: CellWriteHost): CellWrites {
    return new HeldWrites(view, host);
  }
}

/** A cell value's own `id` field (a task's, an event's); `null` when it carries none. */
function valueIdOf(value: unknown): Option<string> {
  if (value == null || typeof value !== "object" || !("id" in value)) return null;
  return typeof value.id === "string" ? value.id : null;
}

/**
 * The days `range` touches, as a half-open day range: from its start's day up
 * to the day after its end's (its end's own day when the end is exactly
 * midnight). Never empty: a range inside one day is that day. Before, the end
 * day was the end instant's own day, so a range within one day (a timed
 * event's 09:00-10:00) named no day at all, and a change-scoped listener
 * (the events table) re-read nothing: a cleared timepoint event stayed held.
 */
function naiveRangeOf(range: DateTime.Range<Utc>): NaiveDate.Range {
  const start = NaiveDate.fromDse(range.start.dse);
  const endDay = NaiveDate.fromDse(range.end.dse);
  const end = range.end.mse % MS_PER_DAY === 0 ? endDay : endDay.addDays(1);
  return new NaiveDate.Range(start, end.dse > start.dse ? end : start.addDays(1));
}

const MS_PER_DAY = 86_400_000;

function timepointCell(dtr: DateTime.Range<Utc>, id: CellId, data: Json | CellValue): RowData.Cell {
  return {
    id,
    date: SemanticTime.fromDateTime(dtr.start),
    endDate: SemanticTime.fromDateTime(dtr.end),
    data,
  };
}

class OwnWrites implements CellWrites {
  readonly #fetched: RowData;
  readonly #host: CellWriteHost;

  constructor(fetched: RowData, host: CellWriteHost) {
    this.#fetched = fetched;
    this.#host = host;
  }

  setDate(date: NaiveDate.Partial, data: LocalCellValue, id: Option<string>, actor: Option<Actor>): void {
    const host = this.#host;
    const range = date.toRange();
    host.recordLocalWrite(host.identityOf(date, id));
    if (actor == null) this.#fetched.upsert(date, data, id);
    else this.#fetched.applyLocalWrite(date, data, actor, id);
    host.dropDerived();
    host.markRealRange(range);
    host.notify(range);
  }

  setDates(edits: readonly { readonly date: NaiveDate.Partial; readonly data: Json | CellValue }[]): void {
    const host = this.#host;
    for (const { date, data } of edits) {
      host.recordLocalWrite(host.identityOf(date, null));
      this.#fetched.upsert(date, data, null);
    }
    host.dropDerived();
    for (const { date } of edits) host.markRealRange(date.toRange());
    host.rebuildAndFire(null);
  }

  setTimepoint(removeId: Option<CellId>, dtr: DateTime.Range<Utc>, id: CellId, data: Json | CellValue, stamp: boolean): void {
    const host = this.#host;
    if (removeId != null) {
      this.#fetched.removeCustom(removeId);
      if (stamp) host.recordLocalWrite("id:" + removeId);
    }
    if (stamp) host.recordLocalWrite("id:" + id);
    this.#fetched.applyBatch([timepointCell(dtr, id, data)]);
    host.dropDerived();
    host.extendFetchedRange(dtr);
    host.notify(null);
  }

  moveTimepoint(newRange: DateTime.Range<Utc>, id: CellId, data: Json | CellValue): void {
    const host = this.#host;
    host.recordLocalWrite("id:" + id);
    this.#fetched.removeCustom(id);
    this.#fetched.applyBatch([timepointCell(newRange, id, data)]);
    host.dropDerived();
    host.extendFetchedRange(newRange);
    host.rebuildAndFire(null);
  }

  clearTimepoint(id: CellId, range: DateTime.Range<Utc>, stamp: boolean): void {
    const host = this.#host;
    if (stamp) host.recordLocalWrite("id:" + id);
    this.#fetched.removeCustom(id);
    host.dropDerived();
    // Even if a handle was attached after the set, the store's clear is a
    // delivered change: preserve its coverage and one listener fire.
    host.extendFetchedRange(range);
    host.notify(naiveRangeOf(range));
  }

  replaceTimepoints(range: DateTime.Range<Utc>, cells: readonly RowData.Cell[]): void {
    const host = this.#host;
    for (const removed of this.#fetched.getAllTimepointsInRange(range)) host.recordLocalWrite("id:" + removed.id);
    for (const cell of cells) if (cell.id != null) host.recordLocalWrite("id:" + cell.id);
    this.#fetched.clearCustomInRange(range);
    if (cells.length > 0) this.#fetched.applyBatch([...cells]);
    host.dropDerived();
    host.extendFetchedRange(range);
    host.notify(naiveRangeOf(range));
  }

  writeAnswer(removeId: Option<CellId>, cells: readonly RowData.Cell[], actor: Option<Actor>): void {
    const host = this.#host;
    if (removeId != null) {
      this.#fetched.removeCustom(removeId);
      host.recordLocalWrite("id:" + removeId);
    }
    for (const cell of cells) {
      if (cell.id != null) host.recordLocalWrite("id:" + cell.id);
    }
    if (cells.length > 0) {
      this.#fetched.applyBatch([...cells]);
      if (actor != null) this.#fetched.stampAnswer([...cells], actor);
    }
    host.dropDerived();
    host.rebuildAndFire(null);
  }

  addressOfExisting(id: CellId, date: NaiveDate.Partial): Option<CellAddress> {
    return this.#fetched.addressOfExisting(id, date);
  }

  remove(addr: CellAddress): Option<DateTime.Range<Utc>> {
    const host = this.#host;
    if (addr.store === "custom") {
      requireNonEmptyKey("RowCellCache.removeCell", "addr.id", addr.id);
      host.recordLocalWrite("id:" + addr.id);
      const dtr = this.#fetched.removeCustom(addr.id);
      if (!dtr) return null;
      host.dropDerived();
      host.rebuildAndFire(naiveRangeOf(dtr));
      return dtr;
    }
    if (addr.store === "multi") {
      // semantic-multi: remove the id-keyed date cell. Returns null (the date
      // lane has no per-cell datetime range).
      requireNonEmptyKey("RowCellCache.removeCell", "addr.id", addr.id);
      host.recordLocalWrite("id:" + addr.id);
      if (this.#fetched.removeMulti(addr.id)) {
        host.dropDerived();
        host.rebuildAndFire(addr.date.toRange());
      }
      return null;
    }
    // standard: clear the date-keyed cell directly. A standard cell can
    // exist on a multi-cardinality row when it was written while the row
    // was single-cardinality and the row later re-resolved to `multi`
    // (the `calendar-events` descriptor applies via applyMeta after an
    // optimistic standard-store write). An upsert rejects the missing id on
    // a multi row, so clear the standard store without routing through the
    // cardinality-gated upsert.
    host.recordLocalWrite(host.identityOf(addr.date, null));
    this.#fetched.clearStandardCell(addr.date);
    host.dropDerived();
    host.rebuildAndFire(addr.date.toRange());
    return null;
  }

  installRead(_decoded: () => readonly RowData.Cell[]): void {}
}

class HeldWrites implements CellWrites {
  readonly #view: HeldCellWrites;
  readonly #host: CellWriteHost;

  constructor(view: HeldCellWrites, host: CellWriteHost) {
    this.#view = view;
    this.#host = host;
  }

  setDate(date: NaiveDate.Partial, data: LocalCellValue, id: Option<string>, actor: Option<Actor>): void {
    const view = this.#view;
    this.#host.viaStore(date.toRange(), () => view.set(date, data, id, actor));
  }

  setDates(edits: readonly { readonly date: NaiveDate.Partial; readonly data: Json | CellValue }[]): void {
    const view = this.#view;
    this.#host.viaStore(null, () => view.batch(() => {
      for (const { date, data } of edits) view.set(date, data, null, null);
    }));
  }

  setTimepoint(removeId: Option<CellId>, dtr: DateTime.Range<Utc>, id: CellId, data: Json | CellValue, _stamp: boolean): void {
    const view = this.#view;
    this.#host.extendFetchedRange(dtr);
    this.#host.viaStore(null, () =>
      view.batch(() => {
        if (removeId != null) view.removeTimepoint(removeId, null);
        view.setTimepoint({ id, dtr, value: data, style: null }, null);
      })
    );
  }

  moveTimepoint(newRange: DateTime.Range<Utc>, id: CellId, data: Json | CellValue): void {
    const view = this.#view;
    this.#host.extendFetchedRange(newRange);
    this.#host.viaStore(null, () =>
      view.batch(() => {
        view.removeTimepoint(id, null);
        view.setTimepoint({ id, dtr: newRange, value: data, style: null }, null);
      })
    );
  }

  clearTimepoint(id: CellId, range: DateTime.Range<Utc>, _stamp: boolean): void {
    const view = this.#view;
    this.#host.extendFetchedRange(range);
    this.#host.viaStore(naiveRangeOf(range), () => view.removeTimepoint(id, null));
  }

  replaceTimepoints(range: DateTime.Range<Utc>, cells: readonly RowData.Cell[]): void {
    const view = this.#view;
    const entries: TimepointCellEntry[] = [];
    for (const cell of cells) {
      const entry = cell.id == null ? null : view.timepointOf(cell);
      if (entry != null) entries.push(entry);
    }
    this.#host.extendFetchedRange(range);
    this.#host.viaStore(naiveRangeOf(range), () => view.replaceTimepoints(range, entries, null));
    this.#host.notify(naiveRangeOf(range));
  }

  writeAnswer(removeId: Option<CellId>, cells: readonly RowData.Cell[], actor: Option<Actor>): void {
    const view = this.#view;
    this.#host.viaStore(null, () =>
      view.batch(() => {
        if (removeId != null) view.removeTimepoint(removeId, actor);
        view.writeAnswer(cells, actor);
      })
    );
  }

  addressOfExisting(id: CellId, date: NaiveDate.Partial): Option<CellAddress> {
    const view = this.#view;
    // The one store, most specific identity first (as `RowData` answers).
    if (view.timepoints(null).some((c) => c.id === id)) return { store: "custom", id };
    const dated = view.dates(null);
    const multi = this.#host.cardinality() === "multi" ? dated.find((c) => c.id === id) : undefined;
    if (multi != null) return { store: "multi", id, date: multi.date };
    const std = dated.find((c) => c.date.type === date.type && c.date.index === date.index && c.id == null);
    if (std != null && valueIdOf(std.value) === id) return { store: "standard", date: std.date };
    return null;
  }

  remove(addr: CellAddress): Option<DateTime.Range<Utc>> {
    const view = this.#view;
    if (addr.store === "custom") {
      const held = view.timepoints(null).find((c) => c.id === addr.id) ?? null;
      if (held == null) return null;
      this.#host.viaStore(naiveRangeOf(held.dtr), () => view.removeTimepoint(addr.id, null));
      return held.dtr;
    }
    const date = addr.date;
    this.#host.viaStore(date.toRange(), () => view.clear(date, addr.store === "multi" ? addr.id : null, null));
    return null;
  }

  installRead(decoded: () => readonly RowData.Cell[]): void {
    const view = this.#view;
    const cells = decoded();
    this.#host.deferred(() => view.install(cells, null));
  }
}
